import { buildFileName, getPatternMetrics } from './config';
import { drawPattern } from './renderer';
import type { GeneratorConfig } from './types';

interface WorkerResponse {
  ok: boolean;
  blob?: Blob;
  error?: string;
}

export async function exportPng(
  config: GeneratorConfig,
  fileName = buildFileName(config),
): Promise<void> {
  const blob = await renderPng(config);
  downloadBlob(blob, normalizeFileName(fileName, '.png'));
}

export function downloadJson(config: GeneratorConfig): void {
  const fileName = buildFileName(config).replace(/\.png$/, '.json');
  downloadBlob(new Blob([JSON.stringify(config, null, 2)], { type: 'application/json' }), fileName);
}

async function renderPng(config: GeneratorConfig): Promise<Blob> {
  if (typeof OffscreenCanvas !== 'undefined' && typeof Worker !== 'undefined') {
    try {
      return await renderInWorker(config);
    } catch {
      // Fall back for browsers with incomplete OffscreenCanvas support.
    }
  }
  return renderOnMainThread(config);
}

async function renderInWorker(config: GeneratorConfig): Promise<Blob> {
  const worker = new Worker(new URL('./export.worker.ts', import.meta.url), { type: 'module' });
  const logo = config.logo.dataUrl
    ? await createImageBitmap(await (await fetch(config.logo.dataUrl)).blob())
    : undefined;

  return new Promise<Blob>((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      worker.terminate();
      logo?.close();
      reject(new Error('PNG export timed out.'));
    }, 120_000);

    worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      window.clearTimeout(timeout);
      worker.terminate();
      if (event.data.ok && event.data.blob) resolve(event.data.blob);
      else reject(new Error(event.data.error || 'PNG export failed.'));
    };
    worker.onerror = () => {
      window.clearTimeout(timeout);
      worker.terminate();
      logo?.close();
      reject(new Error('The background export worker failed.'));
    };
    worker.postMessage({ config, logo }, logo ? [logo] : []);
  });
}

async function renderOnMainThread(config: GeneratorConfig): Promise<Blob> {
  const { resolution } = getPatternMetrics(config);
  const canvas = document.createElement('canvas');
  canvas.width = resolution.width;
  canvas.height = resolution.height;
  const context = canvas.getContext('2d', { alpha: true });
  if (!context) throw new Error('Canvas rendering is unavailable.');
  await document.fonts.ready;
  await drawPattern(context, config);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('The browser could not encode this PNG.'));
    }, 'image/png');
  });
}

function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function normalizeFileName(fileName: string, extension: string): string {
  const sanitized =
    fileName
      .trim()
      .replace(/[<>:"/\\|?*]+/g, '_')
      .split('')
      .filter((character) => character.charCodeAt(0) > 31)
      .join('')
      .replace(/[. ]+$/g, '') || 'ScreenGrid';
  return sanitized.toLowerCase().endsWith(extension) ? sanitized : `${sanitized}${extension}`;
}
